import {Timezone} from 'common/constants';
import {Translatable} from 'common/i18n';
import {TimePeriodSettings} from 'common/report/stores/types';
import {ConversionWindowUnit} from 'common/types/reports/bookmark';
import {
  convertRelativeDateToCalendarDateAsOf,
  getCurrentMomentAsOf,
  NUMBER_OF_DAYS_IN_UNIT,
  UnitAlignmentBehavior,
} from 'common/util/date';

import {END_OF_TODAY} from './presets';
import {
  DateRangeAttributes,
  DateRangeType,
  ExclusionOffset,
  ExclusionPreset,
  INCOMPLETE_CONVERSION_WINDOW_OFFSET,
  INCOMPLETE_PERIOD_OFFSET,
  RelativeDate,
  StandardTimeUnit,
  TimeUnit,
  TimeUnitLabelTranslatable,
} from './types';
import {getNumberOfCalendarDays, getUnitStartTooltip} from './utils';

// Used for unit-less contexts where incomplete period is never compatible (ie dashboards, Events Report, etc)
export const ORDERED_EXCLUSION_PRESETS: ExclusionPreset[] = [
  ExclusionPreset.Today,
  ExclusionPreset.CurrentWeek,
  ExclusionPreset.CurrentMonth,
  ExclusionPreset.CurrentQuarter,
];

// Used for reports that can support unit controls, such as Insights, Funnels, etc.
export const ORDERED_EXCLUSION_PRESETS_WITH_INCOMPLETE_PERIOD: ExclusionPreset[] = [
  ...ORDERED_EXCLUSION_PRESETS,
  ExclusionPreset.IncompletePeriod,
];

// Used for reports with a single funnel metric, which support excluding incomplete conversion windows.
export const EXCLUSION_PRESETS_FOR_FUNNEL_METRIC: ExclusionPreset[] = [
  ...ORDERED_EXCLUSION_PRESETS_WITH_INCOMPLETE_PERIOD,
  ExclusionPreset.IncompleteConversionWindow,
];

// Used for Flows reports, which support excluding incomplete conversion windows but not incomplete periods.
export const EXCLUSION_PRESETS_FOR_FLOWS: ExclusionPreset[] = [
  ...ORDERED_EXCLUSION_PRESETS,
  ExclusionPreset.IncompleteConversionWindow,
];

export const EXCLUSION_PRESET_TO_OFFSET: {
  [key in ExclusionPreset]: ExclusionOffset | null;
} = {
  [ExclusionPreset.Custom]: null,
  [ExclusionPreset.NoExclusion]: null,
  [ExclusionPreset.Today]: {value: 1, unit: TimeUnit.Day},
  [ExclusionPreset.CurrentWeek]: {value: 1, unit: TimeUnit.Week},
  [ExclusionPreset.CurrentMonth]: {value: 1, unit: TimeUnit.Month},
  [ExclusionPreset.CurrentQuarter]: {value: 1, unit: TimeUnit.Quarter},
  [ExclusionPreset.IncompletePeriod]: INCOMPLETE_PERIOD_OFFSET,
  [ExclusionPreset.IncompleteConversionWindow]: INCOMPLETE_CONVERSION_WINDOW_OFFSET,
};

export const SUPPORTED_EXCLUSION_OFFSET_UNITS: TimeUnit[] = [
  TimeUnit.Minute,
  TimeUnit.Hour,
  TimeUnit.Day,
  TimeUnit.Week,
  TimeUnit.Month,
  TimeUnit.Quarter,
];

type ConversionExclusionOptions = {
  conversionWindow: {unit: ConversionWindowUnit; value: number};
  hasMultipleMetrics: boolean;
  hasInsufficientFlowsSteps: boolean;
};

export function getExclusionDisabledReason({
  exclusion,
  isOvertimeChart,
  dateRange,
  asOfDate,
  projectTz,
  timePeriodSettings,
  conversionExclusionOptions,
}: {
  exclusion: ExclusionOffset | null;
  isOvertimeChart: boolean;
  dateRange: DateRangeAttributes | null;
  asOfDate: string | null;
  projectTz: Timezone;
  timePeriodSettings: TimePeriodSettings;
  conversionExclusionOptions?: ConversionExclusionOptions;
}): Translatable | null {
  if (!exclusion) {
    return null;
  }
  if (!dateRange) {
    return ({t}) => t(`reports.date-range.exclusion.disabled.default-dashboard-date-range`);
  }
  // We don't allow excluding for between date ranges other than the TODAY Preset.
  if (dateRange.type === DateRangeType.Between && dateRange.to !== END_OF_TODAY) {
    return ({t}) => t(`reports.date-range.exclusion.disabled.between-date-range`);
  }
  if (exclusion === INCOMPLETE_PERIOD_OFFSET) {
    if (!isOvertimeChart) {
      return ({t}) => t(`reports.date-range.exclusion.disabled.non-line-incomplete-period`);
    }
    return null;
  }

  const asOfMoment = getCurrentMomentAsOf(asOfDate, projectTz);
  let exclusionNumDays: number;

  if (exclusion === INCOMPLETE_CONVERSION_WINDOW_OFFSET) {
    if (conversionExclusionOptions?.hasMultipleMetrics) {
      return ({t}) => t(`reports.date-range.exclusion.disabled.multiple-metrics-conversion-window`);
    }
    const conversionWindow = conversionExclusionOptions?.conversionWindow;
    if (!conversionWindow?.unit || !conversionWindow?.value) {
      return ({t}) => t(`reports.date-range.exclusion.disabled.missing-multiple-flows-steps`);
    }
    if (
      conversionWindow.unit === ConversionWindowUnit.Second ||
      conversionWindow.unit === ConversionWindowUnit.Session
    ) {
      return ({t}) => t(`reports.date-range.exclusion.disabled.unsupported-conversion-unit`);
    }
    if (conversionExclusionOptions?.hasInsufficientFlowsSteps) {
      return ({t}) => t(`reports.date-range.exclusion.disabled.missing-multiple-flows-steps`);
    }
    // A conversion window is a fixed, rolling duration, so convert it directly to days.
    exclusionNumDays =
      conversionWindow.value * NUMBER_OF_DAYS_IN_UNIT[conversionWindow.unit as unknown as StandardTimeUnit];
  } else {
    const exclusionDateStart = convertRelativeDateToCalendarDateAsOf({
      relativeDate: exclusion as RelativeDate,
      asOfDate,
      projectTz,
      timePeriodSettings,
      unitAlignment: UnitAlignmentBehavior.Right,
    });
    exclusionNumDays = asOfMoment.diff(exclusionDateStart, `days`);
  }

  const dateRangeNumDays = getNumberOfCalendarDays(dateRange, asOfMoment, projectTz, timePeriodSettings);
  if (exclusionNumDays >= dateRangeNumDays) {
    return ({t}) => t(`reports.date-range.exclusion.disabled.exclusion-exceeds-date-range`);
  }

  return null;
}

/* keep in sync with EXCLUSION_PRESET_TO_OFFSET and time clause parsing in date_range.py's DateRange.from_dict*/
export function exclusionOffsetToPreset(offset: ExclusionOffset | null | undefined): ExclusionPreset {
  if (!offset) {
    return ExclusionPreset.NoExclusion;
  } else if (offset === INCOMPLETE_PERIOD_OFFSET) {
    return ExclusionPreset.IncompletePeriod;
  } else if (offset === INCOMPLETE_CONVERSION_WINDOW_OFFSET) {
    return ExclusionPreset.IncompleteConversionWindow;
  } else if (typeof offset === `string`) {
    throw new Error(
      `invalid exclusion offset ${offset}. only $incomplete_period, $incomplete_conversion_window, or relative dates are accepted.`,
    );
  }

  if (offset.value === 1) {
    if (offset.unit === TimeUnit.Day) {
      return ExclusionPreset.Today;
    } else if (offset.unit === TimeUnit.Week) {
      return ExclusionPreset.CurrentWeek;
    } else if (offset.unit === TimeUnit.Month) {
      return ExclusionPreset.CurrentMonth;
    } else if (offset.unit === TimeUnit.Quarter) {
      return ExclusionPreset.CurrentQuarter;
    }
  }

  // Custom exclusion window
  return ExclusionPreset.Custom;
}

export const EXCLUSION_MENU_ITEM_LABEL: {[key in ExclusionPreset]: Translatable} = {
  [ExclusionPreset.Custom]: () => ``, // not a valid menu item. TODO: implement UX entry point for custom exclusion
  [ExclusionPreset.NoExclusion]: () => ``, // deselection state. not a valid menu item.
  [ExclusionPreset.Today]: ({t}) => t(`reports.date-range.exclusion.today.menu-item-label`),
  [ExclusionPreset.CurrentWeek]: ({t}) => t(`reports.date-range.exclusion.current-week.menu-item-label`),
  [ExclusionPreset.CurrentMonth]: ({t}) => t(`reports.date-range.exclusion.current-month.menu-item-label`),
  [ExclusionPreset.CurrentQuarter]: ({t}) => t(`reports.date-range.exclusion.current-quarter.menu-item-label`),
  [ExclusionPreset.IncompletePeriod]: ({t}) => t(`reports.date-range.exclusion.incomplete-period.menu-item-label`),
  [ExclusionPreset.IncompleteConversionWindow]: ({t}) =>
    t(`reports.date-range.exclusion.incomplete-conversion-window.menu-item-label`),
};

export function getExcludeSelectedLabel(exclusion: ExclusionOffset | null): Translatable {
  const preset = exclusionOffsetToPreset(exclusion);
  switch (preset) {
    case ExclusionPreset.NoExclusion:
      return ({t}) => t(`reports.date-range.exclusion.title`);
    case ExclusionPreset.Custom: {
      const window = exclusion as RelativeDate;
      return ({t, print}) =>
        t(`reports.date-range.exclusion.custom.selected-label`, {
          unit: print(TimeUnitLabelTranslatable[window.unit](window.value)).toLowerCase(),
          value: window.value,
        });
    }
    default:
      return ({t}) => t(`reports.date-range.exclusion.${preset}.selected-label`);
  }
}

export function getExclusionTooltip(preset: ExclusionPreset, timePeriodSettings: TimePeriodSettings): Translatable {
  switch (preset) {
    case ExclusionPreset.CurrentWeek:
      return getUnitStartTooltip(TimeUnit.Week, timePeriodSettings);
    case ExclusionPreset.CurrentQuarter:
      return getUnitStartTooltip(TimeUnit.Quarter, timePeriodSettings);
    case ExclusionPreset.IncompletePeriod:
      return ({t}) => t(`reports.date-range.exclusion.incomplete-period.tooltip`);
    case ExclusionPreset.IncompleteConversionWindow:
      return ({t}) => t(`reports.date-range.exclusion.incomplete-conversion-window.tooltip`);
  }
  return ``;
}
